import { apiRequest } from "@/services/api";
import type { ChatMessage } from "../scenarios/types";
import type {
  CashFlowRequest,
  Asset,
  Cashflow,
  LifeEvent,
} from "@/types/cash-flow";
import { buildStochasticForecastPayload } from "./cashFlowForecastRequest";

// =============================================================================
// Types
// =============================================================================

export interface CashFlowPlanSummary {
  id: string;
  name: string;
  description?: string;
  client_id?: string;
  created_by_name?: string;
  updated_at: string;
  asset_count: number;
  scenario_count: number;
  goal_count: number;
}

export interface CashFlowSettings {
  projection_years: number;
  surplus_destination: string;
  client_age?: number;
  x_axis_format?: "year" | "age" | "calendar";
}

export type GoalType = 'milestone' | 'retirement';

export interface CashFlowGoal {
  id: string;
  name: string;
  type?: GoalType;
  
  // Milestone goal fields (wealth target by date)
  target_value?: number;
  target_period?: number;
  target_assets?: string[];
  
  // Retirement goal fields (income-based)
  income_target?: number;
  income_start_period?: number;
  longevity_period?: number;
  
  // Calculated field for retirement goals
  derived_wealth_target?: number;
  
  confidence_required?: number;
}

export interface CashFlowScenario {
  id: string;
  name: string;
  description?: string;
  is_baseline: boolean;
  life_events: LifeEvent[];
}

export interface CashFlowPlan {
  id?: string;
  name: string;
  description?: string;
  client_id?: string;
  company_id?: string;
  created_by?: string;
  created_by_name?: string;
  updated_by?: string;
  updated_by_name?: string;
  created_at?: string;
  updated_at?: string;
  settings: CashFlowSettings;
  assets: Asset[];
  cashflows: Cashflow[];
  goals: CashFlowGoal[];
  scenarios: CashFlowScenario[];
}

// =============================================================================
// Chat API (existing)
// =============================================================================

/**
 * Send a message to the cash flow AI expert
 * @param message The user's message
 * @param apiData The current cash flow data on screen
 * @param conversationHistory Previous messages in the conversation
 * @returns The AI expert's response
 */
export async function sendCashFlowChatMessage(
  message: string,
  apiData?: any,
  conversationHistory: ChatMessage[] = []
) {
  return apiRequest({
    method: "POST",
    url: "/ani-experts/cash-flow/chat",
    data: {
      question: message,
      api_data: apiData,
      conversation_history: conversationHistory,
    },
  });
}

// =============================================================================
// Plan CRUD
// =============================================================================

/**
 * Get all cash flow plans
 * @param clientId Optional client ID to filter by
 */
export async function getPlans(
  clientId?: string
): Promise<CashFlowPlanSummary[]> {
  const params = clientId ? { client_id: clientId } : {};
  return apiRequest({
    method: "GET",
    url: "/cashflow/plans",
    params,
  });
}

/**
 * Get a specific cash flow plan by ID
 */
export async function getPlan(planId: string): Promise<CashFlowPlan> {
  return apiRequest({
    method: "GET",
    url: `/cashflow/plans/${planId}`,
  });
}

/**
 * Create a new cash flow plan
 */
export async function createPlan(
  plan: Partial<CashFlowPlan>
): Promise<CashFlowPlan> {
  return apiRequest({
    method: "POST",
    url: "/cashflow/plans",
    data: plan,
  });
}

/**
 * Update an existing cash flow plan
 */
export async function updatePlan(
  planId: string,
  plan: Partial<CashFlowPlan>
): Promise<CashFlowPlan> {
  return apiRequest({
    method: "PUT",
    url: `/cashflow/plans/${planId}`,
    data: plan,
  });
}

/**
 * Delete a cash flow plan
 */
export async function deletePlan(planId: string): Promise<{ success: boolean }> {
  return apiRequest({
    method: "DELETE",
    url: `/cashflow/plans/${planId}`,
  });
}

/**
 * Duplicate a cash flow plan
 */
export async function duplicatePlan(
  planId: string,
  name: string
): Promise<CashFlowPlan> {
  return apiRequest({
    method: "POST",
    url: `/cashflow/plans/${planId}/duplicate`,
    data: { name },
  });
}

// =============================================================================
// Asset CRUD
// =============================================================================

/**
 * Add an asset to a plan
 */
export async function addAsset(planId: string, asset: Asset): Promise<Asset> {
  return apiRequest({
    method: "POST",
    url: `/cashflow/plans/${planId}/assets`,
    data: asset,
  });
}

/**
 * Update an asset in a plan
 */
export async function updateAsset(
  planId: string,
  assetId: string,
  asset: Asset
): Promise<{ success: boolean }> {
  return apiRequest({
    method: "PUT",
    url: `/cashflow/plans/${planId}/assets/${assetId}`,
    data: asset,
  });
}

/**
 * Delete an asset from a plan
 */
export async function deleteAsset(
  planId: string,
  assetId: string
): Promise<{ success: boolean }> {
  return apiRequest({
    method: "DELETE",
    url: `/cashflow/plans/${planId}/assets/${assetId}`,
  });
}

// =============================================================================
// Cashflow CRUD
// =============================================================================

/**
 * Add a cashflow to a plan
 */
export async function addCashflow(
  planId: string,
  cashflow: Cashflow
): Promise<Cashflow> {
  return apiRequest({
    method: "POST",
    url: `/cashflow/plans/${planId}/cashflows`,
    data: cashflow,
  });
}

/**
 * Update a cashflow in a plan
 */
export async function updateCashflow(
  planId: string,
  cashflowId: string,
  cashflow: Cashflow
): Promise<{ success: boolean }> {
  return apiRequest({
    method: "PUT",
    url: `/cashflow/plans/${planId}/cashflows/${cashflowId}`,
    data: cashflow,
  });
}

/**
 * Delete a cashflow from a plan
 */
export async function deleteCashflow(
  planId: string,
  cashflowId: string
): Promise<{ success: boolean }> {
  return apiRequest({
    method: "DELETE",
    url: `/cashflow/plans/${planId}/cashflows/${cashflowId}`,
  });
}

// =============================================================================
// Scenario CRUD
// =============================================================================

/**
 * Add a scenario to a plan
 */
export async function addScenario(
  planId: string,
  scenario: Partial<CashFlowScenario>
): Promise<CashFlowScenario> {
  return apiRequest({
    method: "POST",
    url: `/cashflow/plans/${planId}/scenarios`,
    data: scenario,
  });
}

/**
 * Update a scenario in a plan
 */
export async function updateScenario(
  planId: string,
  scenarioId: string,
  scenario: Partial<CashFlowScenario>
): Promise<{ success: boolean }> {
  return apiRequest({
    method: "PUT",
    url: `/cashflow/plans/${planId}/scenarios/${scenarioId}`,
    data: scenario,
  });
}

/**
 * Delete a scenario from a plan
 */
export async function deleteScenario(
  planId: string,
  scenarioId: string
): Promise<{ success: boolean }> {
  return apiRequest({
    method: "DELETE",
    url: `/cashflow/plans/${planId}/scenarios/${scenarioId}`,
  });
}

// =============================================================================
// Life Event CRUD
// =============================================================================

/**
 * Add a life event to a scenario
 */
export async function addLifeEvent(
  planId: string,
  scenarioId: string,
  event: Omit<LifeEvent, "id">
): Promise<LifeEvent> {
  return apiRequest({
    method: "POST",
    url: `/cashflow/plans/${planId}/scenarios/${scenarioId}/events`,
    data: event,
  });
}

/**
 * Update a life event in a scenario
 */
export async function updateLifeEvent(
  planId: string,
  scenarioId: string,
  eventId: string,
  event: LifeEvent
): Promise<{ success: boolean }> {
  return apiRequest({
    method: "PUT",
    url: `/cashflow/plans/${planId}/scenarios/${scenarioId}/events/${eventId}`,
    data: event,
  });
}

/**
 * Delete a life event from a scenario
 */
export async function deleteLifeEvent(
  planId: string,
  scenarioId: string,
  eventId: string
): Promise<{ success: boolean }> {
  return apiRequest({
    method: "DELETE",
    url: `/cashflow/plans/${planId}/scenarios/${scenarioId}/events/${eventId}`,
  });
}

// =============================================================================
// Goal CRUD
// =============================================================================

/**
 * Add a goal to a plan
 */
export async function addGoal(
  planId: string,
  goal: Partial<CashFlowGoal>
): Promise<CashFlowGoal> {
  return apiRequest({
    method: "POST",
    url: `/cashflow/plans/${planId}/goals`,
    data: goal,
  });
}

/**
 * Update a goal in a plan
 */
export async function updateGoal(
  planId: string,
  goalId: string,
  goal: Partial<CashFlowGoal>
): Promise<{ success: boolean }> {
  return apiRequest({
    method: "PUT",
    url: `/cashflow/plans/${planId}/goals/${goalId}`,
    data: goal,
  });
}

/**
 * Delete a goal from a plan
 */
export async function deleteGoal(
  planId: string,
  goalId: string
): Promise<{ success: boolean }> {
  return apiRequest({
    method: "DELETE",
    url: `/cashflow/plans/${planId}/goals/${goalId}`,
  });
}

// =============================================================================
// Settings
// =============================================================================

/**
 * Update plan settings
 */
export async function updateSettings(
  planId: string,
  settings: CashFlowSettings
): Promise<{ success: boolean }> {
  return apiRequest({
    method: "PUT",
    url: `/cashflow/plans/${planId}/settings`,
    data: settings,
  });
}

// =============================================================================
// Calculations
// =============================================================================

export interface CalculateParams {
  scenario_id?: string;
  goal_id?: string;
}

/**
 * Run deterministic projections on a plan
 */
export async function calculateDeterministic(
  planId: string,
  params?: CalculateParams
): Promise<any> {
  return apiRequest({
    method: "POST",
    url: `/cashflow/plans/${planId}/calculate/deterministic`,
    params,
  });
}

/**
 * Run stochastic projections on a plan
 */
export async function calculateStochastic(
  planId: string,
  params?: CalculateParams
): Promise<any> {
  return apiRequest({
    method: "POST",
    url: `/cashflow/plans/${planId}/calculate/stochastic`,
    params,
  });
}

// =============================================================================
// Forecasting "full" endpoints (same engine as Cash Flow interactive charts)
// =============================================================================

export {
  buildCashFlowRequestFromPlan,
  buildStochasticForecastPayload,
  resolveCashFlowScenario,
  EMPTY_CASH_FLOW_GOAL,
  type CashFlowPlanForecastInput,
  type CashFlowGoalSelection,
} from "./cashFlowForecastRequest";

/**
 * Deterministic projection using the multi-asset forecasting payload
 * (mirrors Cash Flow app `fetchDeterministicData`).
 */
export async function forecastDeterministicFull(
  data: CashFlowRequest
): Promise<any> {
  return apiRequest({
    method: "POST",
    url: "/forecasting/multi-asset/deterministic/full",
    data,
  });
}

/**
 * Stochastic projection using the multi-asset forecasting payload
 * (mirrors Cash Flow app `fetchStochasticData`).
 */
export async function forecastStochasticFull(
  data: CashFlowRequest
): Promise<any> {
  return apiRequest({
    method: "POST",
    url: "/forecasting/multi-asset/stochastic/full",
    data: buildStochasticForecastPayload(data) as CashFlowRequest,
  });
}